import { formatAddress, PortalMode, Error, LoadStatus } from '@servicetitan/cp-ui';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { BodyText } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { FC } from 'react';
import { AppointmentModel } from '../../api';
import { useFormatDate } from '../../utils/use-format-date';
import {
    DashboardGridItemProps,
    DashboardGrid,
    DashboardGridEmptyItemProps,
} from '../ui/grid/grid';
import { EmptyIconCard, SectionIconCard } from '../ui/icon-card/icon-card';
import { InfoLine } from '../ui/info-line';
import { StatusAppointment } from '../ui/status/status';

export const DashboardAppointments: FC<{
    loadingStatus: LoadStatus;
    filtered: boolean;
    mode: PortalMode;
    appointments: AppointmentModel[];
    resetFilters(): void;
}> = ({ appointments, filtered, loadingStatus, mode, resetFilters }) => {
    const formatDate = useFormatDate(true);
    const [{ formatFullTime }] = useDependencies(DateTimeUtils);

    const ItemComponent: FC<DashboardGridItemProps<AppointmentModel>> = ({ item }) => {
        const subAddress = formatAddress({
            ...item.location.address,
            streetAddress: undefined,
            street: undefined,
        });
        return (
            <SectionIconCard
                className={classNames({ 'skeleton-auto': loadingStatus === LoadStatus.Loading })}
                title={item.location.address?.street ?? ''}
                description={subAddress}
                icon="location_on"
                mode={mode}
            >
                <InfoLine icon="build">
                    <BodyText className="c-inherit">{item.jobType.name}</BodyText>
                    <StatusAppointment status={item.status} />
                </InfoLine>

                <InfoLine icon="event" className="m-t-2">
                    <div>
                        <BodyText className="c-inherit">{formatDate(item.startDate)}</BodyText>
                        <BodyText className="c-inherit">
                            {formatFullTime(item.startDate)} - {formatFullTime(item.endDate)}
                        </BodyText>
                    </div>
                </InfoLine>

                <InfoLine icon="info" className="m-t-2">
                    <BodyText className="c-inherit">#{item.number}</BodyText>
                </InfoLine>
            </SectionIconCard>
        );
    };

    const EmptyComponent: FC<DashboardGridEmptyItemProps> = () => (
        <EmptyIconCard
            className={classNames({ 'skeleton-auto': loadingStatus === LoadStatus.Loading })}
            icon="build"
            title="No upcoming appointments"
            description="You don’t have any upcoming appointments"
            resetFilters={resetFilters}
            filtered={filtered}
            mode={mode}
        />
    );

    const ErrorComponent: FC<DashboardGridEmptyItemProps> = () => (
        <Error text="Appointments failed to load" />
    );

    return (
        <DashboardGrid
            mode={mode}
            items={appointments}
            component={ItemComponent}
            emptyComponent={EmptyComponent}
            errorComponent={ErrorComponent}
            loadingStatus={loadingStatus}
        />
    );
};
